import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SiteHeaderProps {
  logoAsHeading?: boolean;
}

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类导航', to: '/#categories' },
  { label: '站点评选', to: '/#ranking' },
  { label: '收录提交', to: '/submit' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader({ logoAsHeading = false }: SiteHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [location.pathname, location.hash]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/?q=${encodeURIComponent(value)}#directory` : '/#directory');
  };

  const logoText = logoAsHeading ? (
    <h1 className="font-heading text-lg md:text-xl font-bold leading-none">爆料导航网</h1>
  ) : (
    <span className="font-heading text-lg md:text-xl font-bold leading-none">爆料导航网</span>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          <Link
            to="/"
            className={`flex items-center gap-2.5 shrink-0 cursor-pointer ${
              transparent ? 'text-background-50' : 'text-foreground-950'
            }`}
            aria-label="爆料导航网首页"
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-lg">
              <i className="ri-flashlight-fill" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col">
              {logoText}
              <span
                className={`font-label text-[10px] tracking-[0.2em] ${
                  transparent ? 'text-background-50/70' : 'text-foreground-500'
                }`}
              >
                分类整理 · 一站收录
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`font-label text-sm px-3 py-2 rounded-md whitespace-nowrap transition-colors ${
                  transparent
                    ? 'text-background-50/90 hover:bg-white/10 hover:text-background-50'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden lg:flex items-center" role="search">
              <div
                className={`flex items-center rounded-full border px-3 h-9 ${
                  transparent
                    ? 'border-white/30 bg-white/10'
                    : 'border-background-200 bg-background-100'
                }`}
              >
                <span
                  className={`w-4 h-4 flex items-center justify-center text-sm ${
                    transparent ? 'text-background-50/80' : 'text-foreground-500'
                  }`}
                >
                  <i className="ri-search-line" aria-hidden="true"></i>
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索站点或领域"
                  aria-label="搜索站点或领域"
                  className={`w-40 bg-transparent text-sm outline-none ml-2 ${
                    transparent
                      ? 'text-background-50 placeholder:text-background-50/60'
                      : 'text-foreground-900 placeholder:text-foreground-400'
                  }`}
                />
              </div>
            </form>

            <Link
              to="/submit"
              className="hidden lg:inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-add-circle-line" aria-hidden="true"></i>
              提交收录
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
              aria-expanded={menuOpen}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-xl transition-colors ${
                transparent
                  ? 'text-background-50 hover:bg-white/10'
                  : 'text-foreground-900 hover:bg-background-100'
              }`}
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="font-label text-sm px-3 py-2.5 rounded-md text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              >
                {item.label}
              </Link>
            ))}
            <form onSubmit={handleSearch} className="mt-2 flex items-center gap-2" role="search">
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索站点或领域"
                aria-label="搜索站点或领域"
                className="flex-1 h-10 px-3 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}